export const PUSH_JOB_TERMINAL_STATUSES = Object.freeze([
  "SUCCEEDED",
  "PARTIALLY_SUCCEEDED",
  "FAILED",
  "CANCELLED",
]);

export const PUSH_JOB_CANCELLABLE_STATUSES = Object.freeze([
  "SCHEDULED",
  "PREPARING",
  "QUEUED",
]);

export const PUSH_JOB_RETRYABLE_STATUSES = Object.freeze([
  "FAILED",
  "PARTIALLY_SUCCEEDED",
  "CANCELLED",
]);

export const PUSH_CATEGORIES = Object.freeze([
  "PROMOTION",
  "LOYALTY",
  "TRANSACTIONAL",
]);

export const PUSH_PLATFORMS = Object.freeze(["ANDROID", "IOS"]);

export const createPushIdempotencyKey = () => {
  if (typeof window !== "undefined" && window.crypto?.randomUUID) {
    return window.crypto.randomUUID();
  }

  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

export const isTerminalPushJob = (status) =>
  PUSH_JOB_TERMINAL_STATUSES.includes(status);

export const canCancelPushJob = (status) =>
  PUSH_JOB_CANCELLABLE_STATUSES.includes(status);

export const canRetryPushJob = (status) =>
  PUSH_JOB_RETRYABLE_STATUSES.includes(status);

export const canResendPushJob = (status) => isTerminalPushJob(status);

export const getPushJobPollingInterval = (job, elapsedMs = 0) => {
  if (!job || isTerminalPushJob(job.status)) {
    return false;
  }

  return elapsedMs < 60_000 ? 3_000 : 10_000;
};

export const getPushStatusLabel = (status, totalRecipients) => {
  if (status === "SUCCEEDED" && totalRecipients === 0) {
    return "Completed — no eligible installations";
  }

  return (
    {
      SCHEDULED: "Scheduled",
      PREPARING: "Preparing audience",
      QUEUED: "Queued",
      PROCESSING: "Sending to Firebase",
      SUCCEEDED: "Succeeded",
      PARTIALLY_SUCCEEDED: "Partially succeeded",
      FAILED: "Failed",
      CANCELLED: "Cancelled",
    }[status] ?? status
  );
};

export const getPushStatusVariant = (status) =>
  ({
    SCHEDULED: "approved",
    PREPARING: "pending",
    QUEUED: "pending",
    PROCESSING: "approved",
    SUCCEEDED: "active",
    PARTIALLY_SUCCEEDED: "archived",
    FAILED: "danger",
    CANCELLED: "inactive",
  }[status] ?? "inactive");

export const validateAdvancedPushDraft = (draft) => {
  const errors = [];
  const titleLength = draft.title.trim().length;
  const messageLength = draft.message.trim().length;

  if (titleLength < 5 || titleLength > 100) {
    errors.push("Title must be between 5 and 100 characters.");
  }
  if (messageLength < 10 || messageLength > 1000) {
    errors.push("Message must be between 10 and 1000 characters.");
  }
  if (!draft.category) {
    errors.push("Select a notification category.");
  }
  if (!draft.platforms?.length) {
    errors.push("Select at least one platform.");
  }

  const audience = draft.audience;
  if (audience?.type === "USERS") {
    const userIds = [...new Set(audience.userIds ?? [])];
    if (userIds.length < 1 || userIds.length > 10_000) {
      errors.push("Select between 1 and 10,000 users.");
    }
  } else if (audience?.type === "SEGMENT") {
    const days = Number(audience.segment?.parameters?.days);
    if (!Number.isInteger(days) || days < 1 || days > 3650) {
      errors.push("Segment days must be an integer between 1 and 3650.");
    }
  } else if (audience?.type !== "ALL") {
    errors.push("Select a notification audience.");
  }

  return errors;
};

export const buildPushPreviewRequest = (draft) => ({
  category: draft.category,
  platforms: [...draft.platforms].sort(),
  audience: draft.audience,
});

export const buildAdvancedPushRequest = (draft) => ({
  title: draft.title.trim(),
  message: draft.message.trim(),
  imageAssetId: draft.imageAsset?.id ?? null,
  ...buildPushPreviewRequest(draft),
  ...(draft.scheduledFor ? { scheduledFor: draft.scheduledFor } : {}),
});

export const toUtcSchedule = (localValue) =>
  localValue ? new Date(localValue).toISOString() : null;

export const isValidPushSchedule = (localValue, now = new Date()) => {
  if (!localValue) {
    return true;
  }

  const scheduled = new Date(localValue);
  const maximum = new Date(now);
  maximum.setFullYear(maximum.getFullYear() + 1);
  return scheduled > now && scheduled <= maximum;
};

export const formatPushDateTime = (value) => {
  if (!value) {
    return "—";
  }

  return new Intl.DateTimeFormat(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
    second: "2-digit",
    timeZoneName: "short",
  }).format(new Date(value));
};
